/**
 * FontSizeContext — manages app-wide base font size preference.
 *
 * Persists the user's choice in localStorage under `yada:font-size`.
 * Applies a `data-font-size` attribute on `<html>` so CSS can scale
 * the base font-size to the user's preferred reading size.
 *
 * Supported values:
 *   'small'   — 14px base (compact)
 *   'medium'  — 16px base (default)
 *   'large'   — 18px base (comfortable)
 *   'xlarge'  — 20px base (accessible)
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type FontSizePreference = 'small' | 'medium' | 'large' | 'xlarge';

interface FontSizeContextValue {
    /** The user-chosen font size preference. */
    fontSize: FontSizePreference;
    /** Update and persist the font size preference. */
    setFontSize: (size: FontSizePreference) => void;
}

const STORAGE_KEY = 'yada:font-size';
const DEFAULT_FONT_SIZE: FontSizePreference = 'medium';

const FONT_SIZE_PX: Record<FontSizePreference, string> = {
    small:  '87.5%',  // 14px
    medium: '100%',   // 16px (browser default)
    large:  '112.5%', // 18px
    xlarge: '125%',   // 20px
};

const FontSizeContext = createContext<FontSizeContextValue>({
    fontSize: DEFAULT_FONT_SIZE,
    setFontSize: () => undefined,
});

function loadFontSize(): FontSizePreference {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'small' || stored === 'medium' || stored === 'large' || stored === 'xlarge') {
            return stored;
        }
    } catch {
        // ignore
    }
    return DEFAULT_FONT_SIZE;
}

function applyFontSize(size: FontSizePreference) {
    document.documentElement.setAttribute('data-font-size', size);
    document.documentElement.style.fontSize = FONT_SIZE_PX[size];
}

export const FontSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [fontSize, setFontSizeState] = useState<FontSizePreference>(loadFontSize);

    // Apply font size to DOM on mount and whenever it changes
    useEffect(() => {
        applyFontSize(fontSize);
    }, [fontSize]);

    // Listen for hydration / cross-tab font size changes
    useEffect(() => {
        const handleFontSizeChange = () => {
            setFontSizeState(loadFontSize());
        };
        window.addEventListener('yada:font-size-change', handleFontSizeChange);
        return () => window.removeEventListener('yada:font-size-change', handleFontSizeChange);
    }, []);

    const setFontSize = useCallback((size: FontSizePreference) => {
        try {
            localStorage.setItem(STORAGE_KEY, size);
        } catch {
            // ignore
        }
        setFontSizeState(size);
        window.dispatchEvent(new CustomEvent('yada:font-size-change'));
        window.dispatchEvent(new CustomEvent('yada:accountSetting:changed'));
    }, []);

    return (
        <FontSizeContext.Provider value={{ fontSize, setFontSize }}>
            {children}
        </FontSizeContext.Provider>
    );
};

export const useFontSize = () => useContext(FontSizeContext);
